import React, { useEffect, useState, useRef, useCallback } from 'react';
import io from 'socket.io-client';

const BACKEND_URL = 'https://api.pacificpearltraining.site';
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const slugify = (name) =>
  (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const AudioMixer = ({ toggleAudioMixer, isAudioMixer, restaurantName }) => {
  const [roomId, setRoomId] = useState(slugify(restaurantName));
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [listeners, setListeners] = useState([]); // [{ listenerId, ip, city, region, country }]
  const [shareUrl, setShareUrl] = useState('');

  const tabStreamRef = useRef(null);
  const relaySocketRef = useRef(null);
  const peerConnectionsRef = useRef({});

  // Auto-fill room ID when restaurant name becomes available
  useEffect(() => {
    if (restaurantName && !roomId) {
      setRoomId(slugify(restaurantName));
    }
  }, [restaurantName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => { stopBroadcasting(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Listener list helpers -------------------------------------------------

  const addListener = useCallback((info) => {
    setListeners(prev => {
      if (prev.find(l => l.listenerId === info.listenerId)) return prev;
      return [...prev, info];
    });
  }, []);

  const removeListener = useCallback((listenerId) => {
    setListeners(prev => prev.filter(l => l.listenerId !== listenerId));
  }, []);

  // ---- WebRTC peer connections -----------------------------------------------

  const createPeerConnection = useCallback((listenerId) => {
    const stream = tabStreamRef.current;
    if (!stream) return;

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    stream.getAudioTracks().forEach(track => {
      pc.addTrack(track, stream);
    });

    pc.onicecandidate = (event) => {
      if (event.candidate && relaySocketRef.current) {
        relaySocketRef.current.emit('ice-candidate', {
          target: listenerId,
          candidate: event.candidate,
        });
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        pc.close();
        delete peerConnectionsRef.current[listenerId];
        removeListener(listenerId);
      }
    };

    peerConnectionsRef.current[listenerId] = pc;

    pc.createOffer()
      .then(offer => pc.setLocalDescription(offer))
      .then(() => {
        relaySocketRef.current.emit('offer', {
          target: listenerId,
          sdp: pc.localDescription,
        });
      })
      .catch(err => console.error('Offer error:', err));
  }, [removeListener]);

  // ---- Start / Stop Broadcasting ---------------------------------------------

  const startBroadcasting = async () => {
    const trimmed = roomId.trim();
    if (!trimmed) return alert('No restaurant room name available.');

    // Capture tab audio via Chrome getDisplayMedia
    let stream;
    try {
      const rawStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          suppressLocalAudioPlayback: true,
        },
        monitorTypeSurfaces: 'exclude',
        systemAudio: 'exclude',
        selfBrowserSurface: 'include',
        surfaceSwitching: 'exclude',
      });

      rawStream.getVideoTracks().forEach(t => t.stop());

      if (rawStream.getAudioTracks().length === 0) {
        alert('No audio was shared. Make sure to check "Share audio" when picking a tab.');
        return;
      }

      stream = new MediaStream(rawStream.getAudioTracks());
    } catch (err) {
      console.error('Tab audio capture cancelled or failed:', err);
      return;
    }

    tabStreamRef.current = stream;

    stream.getAudioTracks()[0].onended = () => {
      stopBroadcasting();
    };

    // Connect to signaling server
    const socket = io(`${BACKEND_URL}/audio-relay`);
    relaySocketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join-room', { roomId: trimmed, role: 'broadcaster' });
      setIsBroadcasting(true);
      setShareUrl(`${BACKEND_URL}/audio/listen/${encodeURIComponent(trimmed)}`);
    });

    // Full list of current listeners (sent when broadcaster joins with existing listeners)
    socket.on('listener-list', (list) => {
      setListeners(list);
      list.forEach(({ listenerId }) => createPeerConnection(listenerId));
    });

    socket.on('listener-joined', ({ listenerId, ip, city, region, country }) => {
      addListener({ listenerId, ip, city, region, country });
      createPeerConnection(listenerId);
    });

    socket.on('answer', ({ from, sdp }) => {
      const pc = peerConnectionsRef.current[from];
      if (pc) pc.setRemoteDescription(new RTCSessionDescription(sdp));
    });

    socket.on('ice-candidate', ({ from, candidate }) => {
      const pc = peerConnectionsRef.current[from];
      if (pc) pc.addIceCandidate(new RTCIceCandidate(candidate));
    });

    socket.on('listener-left', ({ listenerId }) => {
      const pc = peerConnectionsRef.current[listenerId];
      if (pc) pc.close();
      delete peerConnectionsRef.current[listenerId];
      removeListener(listenerId);
    });
  };

  const stopBroadcasting = () => {
    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    peerConnectionsRef.current = {};

    if (tabStreamRef.current) {
      tabStreamRef.current.getTracks().forEach(t => t.stop());
      tabStreamRef.current = null;
    }

    if (relaySocketRef.current) {
      relaySocketRef.current.disconnect();
      relaySocketRef.current = null;
    }

    setIsBroadcasting(false);
    setListeners([]);
    setShareUrl('');
  };

  // ---- Render ----------------------------------------------------------------

  const formatLocation = (l) => {
    const parts = [l.city, l.region, l.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Location unknown';
  };

  return (
    <div style={{ padding: '20px' }}>
      <button
        onClick={toggleAudioMixer}
        style={{ padding: '8px 16px', background: '#546e7a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9em' }}
      >
        Back to Training System
      </button>

      <h1 style={{ margin: '16px 0 20px' }}>Audio Broadcast</h1>

      {!isBroadcasting ? (
        <button
          onClick={startBroadcasting}
          style={{ padding: '14px 32px', fontSize: '1.1em', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
        >
          Start Broadcasting
        </button>
      ) : (
        <button
          onClick={stopBroadcasting}
          style={{ padding: '14px 32px', fontSize: '1.1em', background: '#c62828', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
        >
          Stop Broadcasting
        </button>
      )}

      {shareUrl && (
        <div style={{ marginTop: '20px', padding: '16px', background: '#e8f5e9', borderRadius: '8px', color: '#1b5e20' }}>
          <p style={{ fontWeight: 'bold', marginBottom: '6px' }}>Restaurant listening link:</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
              {shareUrl}
            </a>
            <button
              onClick={() => navigator.clipboard.writeText(shareUrl)}
              style={{ padding: '4px 12px', fontSize: '.85em', background: '#43a047', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Copy
            </button>
          </div>

          {/* ---- Listener list ---- */}
          <div style={{ marginTop: '16px', borderTop: '1px solid #c8e6c9', paddingTop: '12px' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              Listeners ({listeners.length}):
            </p>
            {listeners.length === 0 ? (
              <p style={{ color: '#66bb6a', fontStyle: 'italic', fontSize: '.9em' }}>No one is listening yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {listeners.map((l) => (
                  <div
                    key={l.listenerId}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: '#fff',
                      padding: '10px 14px',
                      borderRadius: '6px',
                      border: '1px solid #c8e6c9',
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: '600', fontSize: '.95em' }}>
                        {formatLocation(l)}
                      </span>
                      <br />
                      <span style={{ fontSize: '.8em', color: '#888' }}>
                        IP: {l.ip || 'unknown'}
                      </span>
                    </div>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#4caf50', flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioMixer;
